import React, { createContext, useContext, useState, useEffect } from "react";
import { supabase } from "../lib/supabase";

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);

  const createUserProfile = async (user) => {
    try {
      const { data, error } = await supabase
        ?.from("user_profiles")
        ?.insert({
          id: user?.id,
          email: user?.email,
          full_name: user?.full_name || user?.email?.split("@")?.[0] || "",
          subscription_tier: "free",
          subscription_status: "inactive",
          max_reports: 1,
          reports_used: 0,
        })
        ?.select()
        ?.single();

      if (!error && data) {
        setUserProfile(data);
      }
    } catch (error) {
      // Don't use console.error for Supabase errors
      if (error?.message?.includes("Failed to fetch")) {
        return;
      }
      console.error("Profile creation error:", error);
    }
  };
  // Separate async operations to avoid breaking auth state handler
  const profileOperations = {
    async load(userId) {
      if (!userId) return;
      setProfileLoading(true);
      try {
        const { data, error } = await supabase
          ?.from("user_profiles")
          ?.select("*")
          ?.eq("id", userId)
          ?.single();

        if (!error && data) {
          setUserProfile(data);
        } else if (error && error?.code === "PGRST116") {
          // Profile doesn't exist, create one
          const { data: userData } = await supabase?.auth?.getUser();
          if (userData?.user) {
            await createUserProfile(userData?.user);
          }
        }
      } catch (error) {
        // Don't use console.error for Supabase errors
        // If it's a connection error, show user-friendly message
        if (
          error?.message?.includes("Failed to fetch") ||
          error?.message?.includes("NetworkError")
        ) {
          // This is a connectivity issue, not a code bug
          return;
        }
        console.error("Profile loading error:", error);
      } finally {
        setProfileLoading(false);
      }
    },

    clear() {
      setUserProfile(null);
      setProfileLoading(false);
    },
  };

  // Auth state handlers - MUST remain synchronous
  const authStateHandlers = {
    onChange: (event, session) => {
      setUser(session?.user ?? null);
      setLoading(false);

      if (session?.user) {
        // Load or create profile
        profileOperations?.load(session?.user?.id);
      } else {
        profileOperations?.clear();
      }
    },
  };

  useEffect(() => {
    // Get initial session
    supabase?.auth?.getSession()?.then(({ data: { session } }) => {
      authStateHandlers?.onChange(null, session);
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase?.auth?.onAuthStateChange(authStateHandlers?.onChange);

    return () => subscription?.unsubscribe();
  }, []);

  const signUp = async (email, password, fullName) => {
    try {
      const { data, error } = await supabase?.auth?.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
          },
        },
      });

      // If signup is successful and user is immediately confirmed
      if (!error && data?.user && !data?.user?.email_confirmed_at) {
        // For development - user needs email confirmation
        // Profile will be created automatically via trigger or auth state change
      }

      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  };

  const signIn = async (email, password) => {
    try {
      const { data, error } = await supabase?.auth?.signInWithPassword({
        email,
        password,
      });
      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  };

  const signOut = async () => {
    // Clear local state immediately so UI updates (header, etc.) even if Supabase is slow or fails
    setUser(null);
    profileOperations?.clear();
    try {
      const { error } = await supabase?.auth?.signOut();
      // Clear Supabase auth from localStorage so refresh doesn't restore the session.
      // Supabase stores session under keys like sb-<project-ref>-auth-token; clear all sb-* auth keys.
      try {
        const keysToRemove = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && (key.startsWith("sb-") && key.includes("-auth-"))) {
            keysToRemove.push(key);
          }
        }
        keysToRemove.forEach((key) => localStorage.removeItem(key));
      } catch (storageErr) {
        // Ignore localStorage errors (e.g. private mode)
      }
      return { error };
    } catch (error) {
      return { error };
    }
  };

  const updateProfile = async (updates) => {
    try {
      if (!user?.id) throw new Error("User not authenticated");

      const { data, error } = await supabase
        ?.from("user_profiles")
        ?.update(updates)
        ?.eq("id", user?.id)
        ?.select()
        ?.single();

      if (!error && data) {
        setUserProfile(data);
      }

      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  };

  const canGenerateReport = () => {
    if (!userProfile) return false;

    // Enterprise tier has unlimited reports
    if (userProfile?.subscription_tier === "enterprise") {
      return true;
    }

    // Check if user has remaining reports
    return userProfile?.reports_used < userProfile?.max_reports;
  };

  const getRemainingReports = () => {
    if (!userProfile) return 0;

    if (userProfile?.subscription_tier === "enterprise") {
      return 999999; // Unlimited
    }

    return Math.max(0, userProfile?.max_reports - userProfile?.reports_used);
  };

  // Check if user has premium subscription
  const hasPremiumAccess = () => {
    return (
      userProfile?.subscription_tier === "pro" ||
      userProfile?.subscription_tier === "enterprise" ||
      userProfile?.subscription_tier === "consultant"
    );
  };

  // Check if subscription is active
  const hasActiveSubscription = () => {
    return userProfile?.subscription_status === "active" && hasPremiumAccess();
  };

  const refreshProfile = async () => {
    if (user?.id) {
      await profileOperations.load(user.id);
    }
  };

  const value = {
    user,
    userProfile,
    loading,
    profileLoading,
    signUp,
    signIn,
    signOut,
    updateProfile,
    refreshProfile,
    canGenerateReport,
    getRemainingReports,
    hasPremiumAccess,
    hasActiveSubscription,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
